import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import Chiclet from '../components/Chiclet/Chiclet';
import AddAppForm from '../components/AddAppForm/AddAppForm';
import Loading from '../components/Loading/Loading';
import EditAppModal from '../components/EditAppModal/EditAppModal';
import { Application, AdminContext, Permission } from '../types';
import useApi from '../hooks/useApi';

const AdminDashboard: React.FC = () => {
  const [appToEdit, setAppToEdit] = useState<Application | null>(null);
  const {
    applications,
    isLoading,
    error,
    handleAppAdded,
    handleAppRemoved,
    handleAppUpdated,
  } = useOutletContext<AdminContext>();
  const { securedRequest } = useApi();

  const [permissions, setPermissions] = useState<Permission[]>([]);

  const fetchPermissions = useCallback(async (signal?: AbortSignal) => {
    const { data, error } = await securedRequest('get', '/api/admin/permissions', null, signal);
    if (data) {
      setPermissions(data);
    }
    if (error && !signal?.aborted) {
      console.error('Failed to fetch permissions:', error);
    }
  }, [securedRequest]);

  useEffect(() => {
    const controller = new AbortController();
    fetchPermissions(controller.signal);
    return () => controller.abort();
  }, [fetchPermissions]);

  if (isLoading && applications.length === 0) {
    return <Loading />;
  }

  return (
    <div className="container mt-5">
      {appToEdit && (
        <EditAppModal 
          app={appToEdit}
          permissions={permissions}
          onClose={() => setAppToEdit(null)} 
          onAppUpdated={handleAppUpdated}
          onAppRemoved={handleAppRemoved}
        />
      )}

      <div className="container mt-5">
        <h2>Manage Applications</h2>
        <p>Add new applications or remove existing ones.</p>

        {error && <div className="alert alert-danger mt-3">Error: Could not fetch data from the API.</div>}

        <AddAppForm onAppAdded={handleAppAdded} />

        <hr />

        <h2>Existing Applications</h2>
        <div className="chiclet-grid">
          {applications.map(app => (
            <Chiclet
              key={app.id}
              app={app}
              onEdit={setAppToEdit}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
